import { useGetSampleCustomersQuery, useGetSegmentQuery } from 'api/segments'
import { useMemo } from 'react'

interface UseSegmentDetailsParams {
  segmentId: string
  enableSampleCustomers?: boolean
  skip?: boolean
  /** Refetch whenever the query becomes active, instead of serving the cache.
   *  Off by default: it costs a request per mount and, where the caller gates
   *  its whole render on `loading`, tears the view down for bytes it already
   *  has. Only the AI builder's details modal needs it — see below. */
  refetchOnMountOrArgChange?: boolean
}

export const useSegmentDetails = ({
  segmentId,
  enableSampleCustomers = true,
  skip = false,
  refetchOnMountOrArgChange = false,
}: UseSegmentDetailsParams) => {
  // The AI can update a segment in place mid-conversation, and nothing
  // invalidates the `TargetSegment` tag, so a caller that reopens the same id
  // needs the refetch or it keeps serving the copy cached the first time
  // (AI-257). Opt-in per caller rather than always on: SegmentPage returns its
  // spinner before it checks `segment`, so forcing it there would blank the
  // whole page on a revisit.
  const segmentQuery = useGetSegmentQuery(
    { segmentId },
    { skip, refetchOnMountOrArgChange }
  )
  const customersQuery = useGetSampleCustomersQuery(
    { segmentId },
    { skip: skip || !enableSampleCustomers }
  )

  // `currentData` (the result for the current arg), not `data` (the last result
  // regardless of arg) — otherwise a segmentId change keeps returning the
  // previously viewed segment until the new request resolves (AI-257).
  const data = useMemo(
    () => ({
      segment: segmentQuery.currentData?.target_segment,
      customers: customersQuery.currentData,
    }),
    [segmentQuery.currentData?.target_segment, customersQuery.currentData]
  )

  // `isFetching` (any in-flight request), not `isLoading` (first load only) —
  // a segmentId change is not a first load, so `isLoading` stays false while
  // the new segment is being fetched.
  const loading = useMemo(
    () => ({
      segment: segmentQuery.isFetching,
      customers: customersQuery.isFetching,
      any: segmentQuery.isFetching || customersQuery.isFetching,
    }),
    [segmentQuery.isFetching, customersQuery.isFetching]
  )

  const error = useMemo(
    () => ({
      segment: segmentQuery.error,
      customers: customersQuery.error,
      any: segmentQuery.error || customersQuery.error,
    }),
    [segmentQuery.error, customersQuery.error]
  )

  return {
    data,
    loading,
    error,
    refetch: {
      segment: segmentQuery.refetch,
      customers: customersQuery.refetch,
    },
  }
}
